import { useCallback, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { authErrors, authLoading, refreshTokenIsValid } from '@/reducers'
import { signUp } from '@/actions/auth'
import { useAppDispatch, useAppSelector, useUrlSearchParam } from '@/hooks'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import {
  redirectToAppleOAuthURL,
  redirectToGoogleOAuthURL,
} from '@/utils/oauth/oauthUtils'
import { logEvent } from '@/logging'
import { trackSignupStarted, trackSignupSubmitted } from '@/logging/segment'
import { logException, logMessage } from '@/logging/sentry'
import { useWhiteLabelData } from '@/hooks/whitelabel/useWhiteLabelData'
import useBasePath from '@/hooks/useBasePath'
import useRemoteConfig from '@/hooks/useRemoteConfig'
import referralUtils from '@/utils/referralUtils/referralUtils'
import { useIsAvaBasePath } from '@/app/hooks/ava/useAvaVerification'
import posthog from 'posthog-js'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import parseTemplate from '@/utils/string/parseTemplate'

const DEFAULT_SIGNUP_TITLE_TEMPLATE =
  'Join ${sponsor_name} ${program_name} and earn rewards for charging your EV at home.'

const DEFAULT_SIGNUP_TITLE_GENERIC =
  'Join today and earn rewards for charging your EV at home.'

function getDefaultSignupTitle(
  sponsorName: string,
  programName: string,
): string {
  if (sponsorName || programName) {
    return parseTemplate(DEFAULT_SIGNUP_TITLE_TEMPLATE, {
      sponsor_name: sponsorName,
      program_name: programName,
    })
  }
  return DEFAULT_SIGNUP_TITLE_GENERIC
}

type SignupForm = {
  name: string
  username: string
  password: string
  referral_code?: string
}

// Yup validation schema
const signupSchema = yup.object({
  name: yup.string().required('Name is required').trim(),
  username: yup
    .string()
    .required('Email is required')
    .email('Please enter a valid email address'),
  password: yup
    .string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters'),
  referral_code: yup.string().optional(),
})

export default function useViewModel(programIdFromRoute?: number | null) {
  const history = useHistory()
  const basePath = useBasePath() ?? ''
  const paramReferralCode = useUrlSearchParam('referral_code') as string
  const {
    whiteLabelData,
    viewConfig,
    isLoading: loadingTheme,
    fetchError,
    hasProgramData,
  } = useWhiteLabelData(programIdFromRoute)
  const {
    appRemoteConfig,
    isLoading: remoteConfigLoading,
    error: remoteConfigError,
  } = useRemoteConfig()
  const programIdFromBasePath =
    appRemoteConfig?.getWhitelabelId(basePath) ?? null
  const programId = programIdFromRoute ?? programIdFromBasePath
  const useCaptcha = !appRemoteConfig?.getBoolean('hide_signup_captcha')
  const [signupSuccess, setSignupSuccess] = useState(false)

  const { register, handleSubmit, watch, formState, setValue } =
    useForm<SignupForm>({
      mode: 'onChange',
      resolver: yupResolver(signupSchema),
      defaultValues: {
        name: '',
        username: '',
        password: '',
        referral_code: paramReferralCode || '',
      },
    })

  const dispatch = useAppDispatch()
  const isAva = useIsAvaBasePath()
  const { executeRecaptcha } = useGoogleReCaptcha()
  const [captchaLoading, setCaptchaLoading] = useState(false)

  const getCaptchaToken = useCallback(async () => {
    if (!executeRecaptcha) {
      return null
    }
    return await executeRecaptcha('signup')
  }, [executeRecaptcha])

  useEffect(() => {
    logEvent('start_sign_up')
    trackSignupStarted()
  }, [])

  useEffect(
    function onReferralCodeParam() {
      if (paramReferralCode) {
        setValue('referral_code', paramReferralCode)
      }
    },
    [paramReferralCode, setValue],
  )

  useEffect(() => {
    if (!loadingTheme && !remoteConfigLoading) {
      posthog.experiments.getWebExperimentsAndEvaluateDisplayLogic(true)
    }
  }, [loadingTheme, remoteConfigLoading])

  useEffect(() => {
    if (remoteConfigError) {
      logException(
        `Failed to load remote config on signup page! Error: ${remoteConfigError}`,
      )
    }
  }, [remoteConfigError])

  useEffect(() => {
    if (programIdFromRoute == null) return
    if (loadingTheme || remoteConfigLoading) return

    const programNotFound =
      Boolean(fetchError) || hasProgramData === false || !viewConfig

    if (programNotFound) {
      history.replace('/signup')
    }
  }, [
    programIdFromRoute,
    loadingTheme,
    remoteConfigLoading,
    viewConfig,
    fetchError,
    hasProgramData,
    history,
  ])

  const errors = useAppSelector((state) => authErrors(state))
  const authenticated = useAppSelector((state) => refreshTokenIsValid(state))
  const loading = useAppSelector((state) => authLoading(state))

  // Use form state to determine if form is valid
  const isFormValid = formState.isValid

  const onSubmit = async (data: SignupForm) => {
    let captchaToken = ''
    if (useCaptcha) {
      setCaptchaLoading(true)
      captchaToken = (await getCaptchaToken()) ?? ''
      setCaptchaLoading(false)
    }

    let utility_program_id = programId

    if (!utility_program_id) {
      utility_program_id = appRemoteConfig?.getWhitelabelId(basePath) ?? null
      if (utility_program_id) {
        logMessage(
          `Program id was found to be null then successfully loaded on second try upon submit: ${utility_program_id}`,
        )
      }
    }

    if (!utility_program_id && data.referral_code) {
      const referralProgramId =
        referralUtils.getWhiteLabelProgramIdFromReferralCode(data.referral_code)
      if (referralProgramId) {
        utility_program_id = referralProgramId
      }
    }

    trackSignupSubmitted('email')
    setSignupSuccess(true)

    // Allow marketing pixel to render and fire before proceeding
    await new Promise((resolve) => setTimeout(resolve, 100))

    dispatch(
      signUp({
        name: data.name,
        username: data.username,
        password: data.password,
        referral_code: data.referral_code || '',
        utility_program_id,
        captcha: captchaToken,
      }),
    )
  }

  const continueWithGoogle = async () => {
    logEvent('signUp')
    trackSignupSubmitted('google')
    setSignupSuccess(true)

    // Allow marketing pixel to render and fire before redirect
    await new Promise((resolve) => setTimeout(resolve, 100))

    const referral_code = watch('referral_code')
    await redirectToGoogleOAuthURL(referral_code)
  }

  const continueWithApple = async () => {
    logEvent('signUp')
    trackSignupSubmitted('apple')
    setSignupSuccess(true)

    // Allow marketing pixel to render and fire before redirect
    await new Promise((resolve) => setTimeout(resolve, 100))

    const referral_code = watch('referral_code')
    await redirectToAppleOAuthURL(referral_code)
  }

  const validLoading = typeof loading !== 'undefined' && loading
  const showSsoLinks = basePath === 'signup' || Boolean(programIdFromRoute)

  const sponsorName = viewConfig?.sponsorName ?? ''
  const programName = viewConfig?.name ?? ''
  const rawSignupText = viewConfig?.signupPage?.signupText
  const templateVars = {
    utility_name: sponsorName,
    sponsor_name: sponsorName,
    program_name: programName,
  }
  const resolvedSignupText = rawSignupText
    ? parseTemplate(rawSignupText, templateVars)
    : getDefaultSignupTitle(sponsorName, programName)

  const titleColor =
    (viewConfig?.signupPage as { titleColor?: string } | undefined)
      ?.titleColor ??
    viewConfig?.colorScheme?.primaryColor ??
    '#af4bfb'

  const isProgramSignup = Boolean(programIdFromRoute)

  // Get field error from form state
  const getFieldError = useCallback(
    (fieldName: keyof SignupForm) => {
      return formState.errors[fieldName]?.message
    },
    [formState.errors],
  )

  return {
    register,
    handleSubmit,
    watch,
    formState,
    setValue,
    getFieldError,
    captchaLoading,
    basePath,
    isAva,
    errors,
    authenticated,
    loading: validLoading,
    showSsoLinks,
    loadingTheme,
    remoteConfigLoading,
    useCaptcha,
    whiteLabelData,
    viewConfig,
    paramReferralCode,
    isFormValid,
    onSubmit,
    continueWithGoogle,
    continueWithApple,
    executeRecaptcha,
    referralCode: watch('referral_code'),
    signupSuccess,
    resolvedSignupText,
    titleColor,
    isProgramSignup,
  }
}
